import { createClient } from '@supabase/supabase-js';
import type { UserProfile } from '../types';

/**
 * Supabase client configuration.
 *
 * SECURITY: credentials must come from environment variables at build time
 * (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY). The anon key is a public
 * client identifier — database protection comes from Row-Level Security, not
 * from key secrecy. Never hardcode keys here or commit real .env files.
 */
const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env || {};
const isDev = Boolean(env.DEV);

// Placeholder used ONLY in local dev/test when env vars are missing, so the
// app still boots for UI development. It cannot reach any real project.
const DEV_PLACEHOLDER_URL = 'https://placeholder.supabase.co';
const DEV_PLACEHOLDER_ANON_KEY = 'dev-placeholder-anon-key-not-a-real-credential';

let supabaseUrl = env.VITE_SUPABASE_URL || '';
let supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY || '';

if ((!supabaseUrl || !supabaseAnonKey) && !isDev) {
  // Production build without credentials: fail fast and loudly instead of
  // silently shipping an app that cannot authenticate.
  throw new Error(
    'Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. ' +
    'Set them as environment variables at build time (see DEPLOY_CHECKLIST.md).'
  );
}

if (!supabaseUrl) supabaseUrl = DEV_PLACEHOLDER_URL;
if (!supabaseAnonKey) supabaseAnonKey = DEV_PLACEHOLDER_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Get current user's profile
 */
export async function getCurrentUserProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) return null;
  
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profile) return profile as UserProfile;

    // Fallback: bridged local accounts have store ids (usr-…) that differ from
    // the Supabase Auth UUID, so match the profile row by email instead.
    if (user.email) {
      const { data: byEmail } = await supabase
        .from('user_profiles')
        .select('*')
        .ilike('email', user.email)
        .limit(1)
        .maybeSingle();
      if (byEmail) return byEmail as UserProfile;
    }
  } catch (err) {
    console.warn('Could not fetch user profile from database:', err);
  }

  // Fallback: derive profile from user metadata or email
  const meta = user.user_metadata || {};
  const email = (user.email || '').toLowerCase();
  let role = (meta.role || 'sales_rep') as UserProfile['role'];
  
  if (email.includes('admin')) role = 'admin';
  else if (email.includes('soly') || email.includes('bhalla')) role = 'executive';
  else if (email.includes('manager')) role = 'manager';
  else if (email.includes('accountant')) role = 'accountant';
  else if (email.includes('marketing')) role = 'marketing';
  else if (email.includes('salesrep') || email.includes('sales')) role = 'sales_rep';

  return {
    id: user.id,
    email: user.email || '',
    name: meta.name || user.email?.split('@')[0] || 'Team Member',
    role,
    is_active: meta.is_active !== false,
    can_approve_discounts: role === 'admin' || role === 'executive' || role === 'manager',
    max_discount_limit: role === 'admin' || role === 'executive' ? 100 : (role === 'manager' ? 20 : 10),
    created_at: user.created_at,
  } as UserProfile;
}

/**
 * Check if user has required role
 * @param userRole The current user's role
 * @param requiredRole The minimum role required for access
 */
export function hasRole(userRole?: string, requiredRole?: string): boolean {
  const roleHierarchy = ['sales_rep', 'marketing', 'accountant', 'manager', 'executive', 'admin'];
  
  if (!userRole || !requiredRole) return false;
  
  const userIndex = roleHierarchy.indexOf(userRole);
  const requiredIndex = roleHierarchy.indexOf(requiredRole);
  
  if (userIndex === -1 || requiredIndex === -1) return userRole === requiredRole;
  return userIndex >= requiredIndex;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) throw error;
  return data;
}

/**
 * Sign up new user
 */
export async function signUp(email: string, password: string, name: string, role: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        name,
        role,
      },
    },
  });
  
  if (error) throw error;
  return data;
}

/**
 * Sign out
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Reset password
 */
export async function resetPassword(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  
  if (error) throw error;
}
